import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { api, useAuth } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  TrendingUp, TrendingDown, PieChart, BarChart3, Activity,
  Calendar, RefreshCw, ArrowUpRight, ArrowDownRight, Target, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, PieChart as RechartsPie, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const COLORS = ['#00E676', '#6366F1', '#F7931A', '#FF3B30', '#2962FF', '#9333EA'];

export default function PortfolioAnalytics() {
  const { user } = useAuth();
  const { formatCurrency, preferredCurrency } = useCurrency();
  const [portfolio, setPortfolio] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('1M');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      const [portfolioRes, analyticsRes] = await Promise.all([
        api.get('/portfolio'),
        api.get(`/portfolio/analytics?time_range=${timeRange}`)
      ]);
      setPortfolio(portfolioRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error(error);
      // Generate mock data if API not available
      generateMockAnalytics();
    } finally {
      setLoading(false);
    }
  };

  const generateMockAnalytics = () => {
    // Mock portfolio data
    const mockPortfolio = {
      total_value: 12450.50,
      cash_balance: user?.balance || 1000,
      assets: [
        { symbol: 'AAPL', name: 'Apple Inc', quantity: 10, current_price: 185.50, avg_cost: 170.00, value: 1855.00 },
        { symbol: 'BTC', name: 'Bitcoin', quantity: 0.5, current_price: 45000, avg_cost: 40000, value: 22500.00 },
        { symbol: 'ETH', name: 'Ethereum', quantity: 5, current_price: 2500, avg_cost: 2200, value: 12500.00 },
        { symbol: 'GOLD', name: 'Gold', quantity: 2, current_price: 2000, avg_cost: 1900, value: 4000.00 },
        { symbol: 'MSFT', name: 'Microsoft', quantity: 8, current_price: 380, avg_cost: 350, value: 3040.00 }
      ]
    };
    setPortfolio(mockPortfolio);

    // Mock analytics data
    const days = timeRange === '1W' ? 7 : timeRange === '1M' ? 30 : timeRange === '3M' ? 90 : 365;
    const performanceData = [];
    let value = 10000;
    for (let i = days; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      value = value * (1 + (Math.random() - 0.48) * 0.02);
      performanceData.push({
        date: date.toISOString().split('T')[0],
        value: Math.round(value * 100) / 100,
        benchmark: 10000 * Math.pow(1.0003, days - i)
      });
    }

    const mockAnalytics = {
      performance: performanceData,
      total_return: ((value - 10000) / 10000 * 100).toFixed(2),
      total_return_amount: value - 10000,
      daily_return: (Math.random() * 4 - 2).toFixed(2),
      weekly_return: (Math.random() * 10 - 5).toFixed(2),
      monthly_return: (Math.random() * 20 - 10).toFixed(2),
      volatility: (Math.random() * 20 + 10).toFixed(2),
      sharpe_ratio: (Math.random() * 2 + 0.5).toFixed(2),
      max_drawdown: -(Math.random() * 15 + 5).toFixed(2),
      win_rate: (Math.random() * 30 + 50).toFixed(1),
      allocation: [
        { name: 'Stocks', value: 35, color: '#00E676' },
        { name: 'Crypto', value: 40, color: '#F7931A' },
        { name: 'Metals', value: 15, color: '#FFD700' },
        { name: 'Cash', value: 10, color: '#6366F1' }
      ],
      top_performers: [
        { symbol: 'BTC', return: 25.5 },
        { symbol: 'ETH', return: 18.2 },
        { symbol: 'AAPL', return: 12.8 }
      ],
      worst_performers: [
        { symbol: 'MSFT', return: -5.2 },
        { symbol: 'GOLD', return: -2.1 }
      ],
      monthly_returns: [
        { month: 'Jul', return: 5.2 },
        { month: 'Aug', return: -2.1 },
        { month: 'Sep', return: 8.4 },
        { month: 'Oct', return: -1.5 },
        { month: 'Nov', return: 12.3 },
        { month: 'Dec', return: 3.7 }
      ]
    };
    setAnalytics(mockAnalytics);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics();
    setRefreshing(false);
    toast.success('Analytics refreshed');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="portfolio-analytics">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Portfolio Analytics
            </h1>
            <p className="text-gray-400 mt-1">Performance metrics and insights</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="input-dark w-32">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="1W">1 Week</SelectItem>
                <SelectItem value="1M">1 Month</SelectItem>
                <SelectItem value="3M">3 Months</SelectItem>
                <SelectItem value="1Y">1 Year</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleRefresh} disabled={refreshing} className="btn-secondary">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Total Return</span>
                {parseFloat(analytics?.total_return) >= 0 ? (
                  <ArrowUpRight className="w-5 h-5 text-[#00E676]" />
                ) : (
                  <ArrowDownRight className="w-5 h-5 text-[#FF3B30]" />
                )}
              </div>
              <p className={`text-2xl font-bold number ${parseFloat(analytics?.total_return) >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                {parseFloat(analytics?.total_return) >= 0 ? '+' : ''}{analytics?.total_return}%
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {formatCurrency(analytics?.total_return_amount)}
              </p>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Volatility</span>
                <Activity className="w-5 h-5 text-[#F7931A]" />
              </div>
              <p className="text-2xl font-bold text-white number">{analytics?.volatility}%</p>
              <p className="text-xs text-gray-500 mt-1">Annualized</p>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Sharpe Ratio</span>
                <Target className="w-5 h-5 text-[#6366F1]" />
              </div>
              <p className="text-2xl font-bold text-white number">{analytics?.sharpe_ratio}</p>
              <p className="text-xs text-gray-500 mt-1">Risk-adjusted return</p>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-400 text-sm">Max Drawdown</span>
                <Shield className="w-5 h-5 text-[#FF3B30]" />
              </div>
              <p className="text-2xl font-bold text-[#FF3B30] number">{analytics?.max_drawdown}%</p>
              <p className="text-xs text-gray-500 mt-1">Largest decline</p>
            </CardContent>
          </Card>
        </div>

        {/* Performance Chart */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Portfolio Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics?.performance || []}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00E676" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#00E676" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="date" stroke="#666" tick={{ fontSize: 12 }} />
                  <YAxis stroke="#666" tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v/1000).toFixed(1)}K`} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid #333', borderRadius: '8px' }}
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Value']}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="value" name="Portfolio" stroke="#00E676" fillOpacity={1} fill="url(#colorValue)" />
                  <Line type="monotone" dataKey="benchmark" name="S&P 500" stroke="#6366F1" strokeDasharray="5 5" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for detailed analytics */}
        <Tabs defaultValue="allocation" className="w-full">
          <TabsList className="bg-white/5 border border-white/10 p-1">
            <TabsTrigger value="allocation" className="data-[state=active]:bg-[#6366F1] data-[state=active]:text-white">
              <PieChart className="w-4 h-4 mr-2" /> Allocation
            </TabsTrigger>
            <TabsTrigger value="returns" className="data-[state=active]:bg-[#6366F1] data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4 mr-2" /> Returns
            </TabsTrigger>
            <TabsTrigger value="holdings" className="data-[state=active]:bg-[#6366F1] data-[state=active]:text-white">
              <Activity className="w-4 h-4 mr-2" /> Holdings
            </TabsTrigger>
          </TabsList>

          {/* Allocation Tab */}
          <TabsContent value="allocation" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Asset Allocation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPie>
                        <Pie
                          data={analytics?.allocation || []}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {(analytics?.allocation || []).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid #333', borderRadius: '8px' }}
                          formatter={(value) => [`${value}%`, 'Allocation']}
                        />
                        <Legend />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Performance by Asset
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-400 mb-2">Top Performers</p>
                      {(analytics?.top_performers || []).map((asset, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-white/5">
                          <span className="text-white font-medium">{asset.symbol}</span>
                          <span className="text-[#00E676] number">+{asset.return}%</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-2">Worst Performers</p>
                      {(analytics?.worst_performers || []).map((asset, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-white/5">
                          <span className="text-white font-medium">{asset.symbol}</span>
                          <span className="text-[#FF3B30] number">{asset.return}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Returns Tab */}
          <TabsContent value="returns" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Monthly Returns
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analytics?.monthly_returns || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="month" stroke="#666" />
                        <YAxis stroke="#666" tickFormatter={(v) => `${v}%`} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid #333', borderRadius: '8px' }}
                          formatter={(value) => [`${value}%`, 'Return']}
                        />
                        <Bar dataKey="return" fill="#00E676">
                          {(analytics?.monthly_returns || []).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.return >= 0 ? '#00E676' : '#FF3B30'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-dashboard">
                <CardHeader>
                  <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Return Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { label: 'Daily Return', value: analytics?.daily_return, period: 'Today' },
                    { label: 'Weekly Return', value: analytics?.weekly_return, period: '7 days' },
                    { label: 'Monthly Return', value: analytics?.monthly_return, period: '30 days' },
                    { label: 'Total Return', value: analytics?.total_return, period: timeRange }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                      <div>
                        <p className="text-white font-medium">{item.label}</p>
                        <p className="text-xs text-gray-500">{item.period}</p>
                      </div>
                      <p className={`text-xl font-bold number ${parseFloat(item.value) >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                        {parseFloat(item.value) >= 0 ? '+' : ''}{item.value}%
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Holdings Tab */}
          <TabsContent value="holdings" className="mt-6">
            <Card className="card-dashboard">
              <CardHeader>
                <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Current Holdings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Asset</th>
                        <th className="text-right py-3 px-4 text-gray-400 font-medium">Quantity</th>
                        <th className="text-right py-3 px-4 text-gray-400 font-medium">Avg Cost</th>
                        <th className="text-right py-3 px-4 text-gray-400 font-medium">Current Price</th>
                        <th className="text-right py-3 px-4 text-gray-400 font-medium">Value</th>
                        <th className="text-right py-3 px-4 text-gray-400 font-medium">P/L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(portfolio?.assets || []).map((asset, i) => {
                        const pl = ((asset.current_price - asset.avg_cost) / asset.avg_cost * 100).toFixed(2);
                        const plValue = (asset.current_price - asset.avg_cost) * asset.quantity;
                        return (
                          <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-3 px-4">
                              <div>
                                <p className="text-white font-medium">{asset.symbol}</p>
                                <p className="text-xs text-gray-500">{asset.name}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right text-white number">{asset.quantity}</td>
                            <td className="py-3 px-4 text-right text-gray-400 number">{formatCurrency(asset.avg_cost)}</td>
                            <td className="py-3 px-4 text-right text-white number">{formatCurrency(asset.current_price)}</td>
                            <td className="py-3 px-4 text-right text-white number">{formatCurrency(asset.value)}</td>
                            <td className={`py-3 px-4 text-right number ${parseFloat(pl) >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'}`}>
                              {parseFloat(pl) >= 0 ? '+' : ''}{pl}%
                              <p className="text-xs">{formatCurrency(plValue)}</p>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Risk Metrics */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Risk Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-white/5">
                <p className="text-sm text-gray-400">Win Rate</p>
                <p className="text-2xl font-bold text-[#00E676] number">{analytics?.win_rate}%</p>
                <p className="text-xs text-gray-500">Profitable trades</p>
              </div>
              <div className="p-4 rounded-xl bg-white/5">
                <p className="text-sm text-gray-400">Volatility</p>
                <p className="text-2xl font-bold text-white number">{analytics?.volatility}%</p>
                <p className="text-xs text-gray-500">Price fluctuation</p>
              </div>
              <div className="p-4 rounded-xl bg-white/5">
                <p className="text-sm text-gray-400">Sharpe Ratio</p>
                <p className="text-2xl font-bold text-white number">{analytics?.sharpe_ratio}</p>
                <p className="text-xs text-gray-500">&gt;1 is good, &gt;2 is great</p>
              </div>
              <div className="p-4 rounded-xl bg-white/5">
                <p className="text-sm text-gray-400">Max Drawdown</p>
                <p className="text-2xl font-bold text-[#FF3B30] number">{analytics?.max_drawdown}%</p>
                <p className="text-xs text-gray-500">Worst decline</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
